import { apportionAmount } from "../../../CrossYACalculationSummary";
import { buildIrasMainCompSections } from "../buildIrasMainCompSections";
import { createIrasPdfGenerator } from "../layoutConfig";
import { renderIrasCrossYAChargeableIncomeTable } from "./renderIrasCrossYAChargeableIncomeTable";
import { renderIrasCrossYAMainCompSections } from "./renderIrasCrossYAMainCompSections";

const addSplitAmounts = (rows = [], crossYAApportionmentContext) =>
  (rows || []).map((row) => ({
    ...row,
    preceding_amount: Object.prototype.hasOwnProperty.call(
      row || {},
      "preceding_amount",
    )
      ? row.preceding_amount
      : apportionAmount(
          row?.amount,
          crossYAApportionmentContext,
          "preceding_amount",
        ),
    current_amount: Object.prototype.hasOwnProperty.call(
      row || {},
      "current_amount",
    )
      ? row.current_amount
      : apportionAmount(
          row?.amount,
          crossYAApportionmentContext,
          "current_amount",
        ),
  }));

const addReferenceSplitAmounts = (
  referenceData = {},
  crossYAApportionmentContext,
) =>
  Object.keys(referenceData || {}).reduce((nextReferenceData, key) => {
    const value = referenceData[key];
    nextReferenceData[key] = Array.isArray(value)
      ? addSplitAmounts(value, crossYAApportionmentContext)
      : value;
    return nextReferenceData;
  }, {});

const isGeneratedCrossYACurrentYearLossRow = (row) =>
  row?.row_id === "auto-preceding-ya-current-year-loss" ||
  row?.name === "Current Year Losses - Preceding YA" ||
  row?.reference === "Current Year Losses - Preceding YA" ||
  row?.reference === "[Auto Generated] Current Year Losses";

const addCurrentYALossesBroughtForward = ({
  company,
  computedSummary,
  currencyRate,
  rows,
}) => {
  const lossesBroughtForward = Math.abs(
    parseFloat(
      computedSummary?.current?.yearLossesBroughtForward ||
        computedSummary?.preceding?.currentYearLosses,
    ) || 0,
  );
  const filteredRows = rows.filter(
    (row) => !isGeneratedCrossYACurrentYearLossRow(row),
  );

  if (lossesBroughtForward <= 0) return filteredRows;

  const precedingFinancialYear = Number(company?.yearOfAssessment) - 2;
  return [
    ...filteredRows,
    {
      row_id: "auto-preceding-ya-current-year-loss",
      date: Number.isFinite(precedingFinancialYear)
        ? new Date(precedingFinancialYear, 0)
        : null,
      amount: lossesBroughtForward,
      preceding_amount: 0,
      current_amount: lossesBroughtForward,
      reference: "Current Year Losses - Preceding YA",
      exchangeRate: currencyRate ?? company?.currencyRate,
      systemData: true,
      isForfeited: false,
    },
  ];
};

const generateIrasCrossYAMainCompPdf = ({
  forView,
  company,
  currency,
  currencyRate,
  computedSummary,
  corporateIncomeTaxRebate,
  currentYearRebate,
  netProfitBeforeTax,
  referenceData,
  unutilisedCapitalAllowances,
  unutilisedLosses,
  unutilisedDonations,
  crossYAApportionmentContext,
  isStartup,
  reviewNote,
  taxPreviouslyAssessed,
  isExcel = false,
  pdfDensity = "default",
  pdfSpacingOptions,
  scheduleName = "Main Tax Computation",
}) => {
  const pdf = createIrasPdfGenerator(isExcel, true);
  const schedule = { name: scheduleName };
  const currencyView = currency ?? company.currency;
  const isCompact = pdfDensity === "compact";
  const tableSpacingOptions = {
    density: pdfDensity,
    overrides: pdfSpacingOptions,
  };
  const splitReferenceData = addReferenceSplitAmounts(
    referenceData,
    crossYAApportionmentContext,
  );
  const splitUnutilisedCapitalAllowances = addSplitAmounts(
    unutilisedCapitalAllowances,
    crossYAApportionmentContext,
  );
  const splitUnutilisedLosses = addCurrentYALossesBroughtForward({
    company,
    computedSummary,
    currencyRate,
    rows: addSplitAmounts(unutilisedLosses, crossYAApportionmentContext),
  });
  const splitUnutilisedDonations = addSplitAmounts(
    unutilisedDonations,
    crossYAApportionmentContext,
  );
  const buildSections = (amountKey) =>
    buildIrasMainCompSections({
      amountKey,
      netProfitBeforeTax: apportionAmount(
        netProfitBeforeTax,
        crossYAApportionmentContext,
        amountKey,
      ),
      referenceData: splitReferenceData,
      unutilisedCapitalAllowances: splitUnutilisedCapitalAllowances,
      unutilisedDonations: splitUnutilisedDonations,
      unutilisedLosses: splitUnutilisedLosses,
    });

  const renderResult = renderIrasCrossYAMainCompSections({
    company,
    currencyRate,
    currencyView,
    currentSections: buildSections("current_amount"),
    forView,
    isCompact,
    pdf,
    precedingSections: buildSections("preceding_amount"),
    reviewNote,
    schedule,
    tableSpacingOptions,
    unutilisedCapitalAllowances: splitUnutilisedCapitalAllowances,
    unutilisedDonations: splitUnutilisedDonations,
    unutilisedLosses: splitUnutilisedLosses,
  });
  const splitTaxPreviouslyAssessed = addSplitAmounts(
    taxPreviouslyAssessed,
    crossYAApportionmentContext,
  );

  renderIrasCrossYAChargeableIncomeTable({
    chargeableIncomeBeforeExemptAmount:
      renderResult.chargeableIncomeBeforeExemptAmount,
    company,
    computedSummary,
    corporateIncomeTaxRebate,
    currentYearRebate,
    forView,
    isCompact,
    isStartup,
    mainCompBodyRowCount: renderResult.bodyRowCount,
    pdf,
    reviewNote: renderResult.reviewNote,
    schedule,
    tableSpacingOptions,
    taxPreviouslyAssessed: splitTaxPreviouslyAssessed,
  });

  return pdf.getDoc(forView, company, schedule);
};

export default generateIrasCrossYAMainCompPdf;
